import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCasinoWallet, claimDaily, getCasinoLeaderboard } from "../lib/api";

function fmtCountdown(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export default function Casino() {
  const [wallet, setWallet] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const countdownRef = useRef(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [w, lb] = await Promise.all([getCasinoWallet(), getCasinoLeaderboard()]);
      setWallet(w);
      setLeaderboard(lb);
      countdownRef.current = w.secondsUntilClaim;
      setCountdown(w.secondsUntilClaim);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const iv = setInterval(() => {
      countdownRef.current = Math.max(0, countdownRef.current - 1);
      setCountdown(countdownRef.current);
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const onClaim = async () => {
    setClaiming(true);
    try {
      const w = await claimDaily();
      setWallet(w);
      countdownRef.current = w.secondsUntilClaim;
      setCountdown(w.secondsUntilClaim);
    } catch {} finally { setClaiming(false); }
  };

  const canClaim = countdown <= 0;

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <button className="text-sm text-neutral-500" onClick={() => navigate("/")}>← back</button>
        <h1 className="text-3xl font-mono">casino</h1>
        <div style={{width:48}} />
      </div>

      {wallet && (
        <div className="px-6 pb-4">
          <div className="flex gap-4 justify-center">
            <div className="border border-neutral-200 rounded-2xl px-5 py-3 text-center flex-1">
              <div className="text-[11px] text-neutral-500 uppercase tracking-wide">tokens</div>
              <div className="text-2xl font-mono">{wallet.tokens}</div>
            </div>
            <div className="border border-neutral-200 rounded-2xl px-5 py-3 text-center flex-1">
              <div className="text-[11px] text-neutral-500 uppercase tracking-wide">coins</div>
              <div className="text-2xl font-mono">{wallet.coins}</div>
            </div>
          </div>

          <button
            className={`w-full mt-3 rounded-full py-3 text-sm font-medium transition-colors ${canClaim ? "bg-black text-white" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
            disabled={!canClaim || claiming}
            onClick={onClaim}
          >
            {canClaim ? "Claim 100 free coins" : `Next claim in ${fmtCountdown(countdown)}`}
          </button>
        </div>
      )}

      <div className="px-6 pb-4">
        <div className="text-xs text-neutral-500 uppercase tracking-wide mb-2">games</div>
        <div className="flex gap-3">
          <button
            className="flex-1 border border-neutral-200 rounded-2xl p-5 text-left bg-white shadow-sm active:scale-[0.98] transition-transform"
            onClick={() => navigate("/casino/slots")}
          >
            <div className="text-2xl mb-1">🎰</div>
            <div className="font-mono text-base">Slots</div>
            <div className="text-[11px] text-neutral-500 mt-1">Pure luck · up to 100x</div>
          </button>
          <button
            className="flex-1 border border-neutral-200 rounded-2xl p-5 text-left bg-white shadow-sm active:scale-[0.98] transition-transform"
            onClick={() => navigate("/casino/blackjack")}
          >
            <div className="text-2xl mb-1">🃏</div>
            <div className="font-mono text-base">Blackjack</div>
            <div className="text-[11px] text-neutral-500 mt-1">Skill · beat the dealer</div>
          </button>
        </div>
      </div>

      <div className="px-6 flex-1">
        <div className="text-xs text-neutral-500 uppercase tracking-wide mb-2">leaderboard</div>
        <div className="border border-neutral-200 rounded-2xl overflow-hidden">
          {leaderboard.length === 0 ? (
            <div className="p-4 text-sm text-neutral-400 text-center">No data yet</div>
          ) : (
            leaderboard.map((row, i) => (
              <div key={i} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? "border-t border-neutral-200" : ""}`}>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono w-6 text-neutral-400">{i + 1}.</span>
                  <span className="font-mono text-sm">{row.username}</span>
                </div>
                <span className="font-mono text-sm text-neutral-600">{row.coins} coins</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="h-6" />
    </div>
  );
}
